import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import Swal from 'sweetalert2';

import { EventType } from '@app/shared/constants-server';
import type { RentalWindowDecision } from '@app/shared/_models/sales';
import type { Day, RentalLineView } from '../rental-billing.model';
import { CLOSED_LINE, lineClosed, patientLabel, statusLabel } from '../rental-billing.labels';
import { tooManyDecisions } from '../rental-billing.service';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import { RentalDecisionEvent, decisionCancel, windowAdd } from '../rental-decision-events';
import type { WindowDialogData } from '../rental-dialogs.contract';
import { addMonthsClamped, firstOfMonth, frDay, frShort, frWeekday, minDay } from '../rental-days';
import { PickerMarks, pickerMarksOf } from '../rental-picker.geometry';
import { deliveredDay, memoGroupsOf, returnedDay } from '../rental-planning.geometry';
import { RentalDayPickerComponent } from '../day-picker/rental-day-picker.component';
import { RentalEffectComponent } from '../effect/rental-effect.component';
import { ReasonChip, RentalDecisionDialogBase, cancelledText, lineName, memoTwins, monthShowing, reasonChips, stampText, windowTwinKey } from './rental-decision-dialog.base';

interface ScopeLine { id: number; name: string; note: string; why: string | null; }
interface ScopeGroup { key: string; label: string; lines: ScopeLine[]; }
interface BoundEntry { id: number; what: string; stamp: string; cancelled: string | null; effective: boolean; }
interface LineHead { id: number; name: string; memo: string | null; facts: string; current: string; effectiveId: number | null; history: BoundEntry[]; }

// Début avant le BL (START) ou fin de facturation (END) d'une ou plusieurs lignes : évènements 2103 (E.6).
// day : START = premier jour facturé ; END = DERNIER jour facturé, inclus (depuis le 27/10/2026 ; avant, le premier jour sans facturation).
// L'écran montre ce dernier jour tel quel, jamais son lendemain (règle du 27/10/2026).
@Component({
  selector: 'app-rental-window-dialog',
  templateUrl: './rental-window-dialog.component.html',
  styleUrls: ['./rental-window-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatDialogModule, MatProgressSpinnerModule, RentalDayPickerComponent, RentalEffectComponent],
})
export class RentalWindowDialogComponent extends RentalDecisionDialogBase<WindowDialogData> {
  protected readonly dialogs = inject(RentalDecisionDialogsService);
  readonly bound = this.data.bound;
  readonly title = this.bound === 'START' ? 'Corriger le début (avant le BL)' : 'Fin de facturation';

  checked = new Set<number>();
  day: Day | null = null;
  reasonId: number | null = null;
  comment = '';
  replaceReason = '';
  leftMonth: Day | null = null;

  groups: ScopeGroup[] = [];
  heads: LineHead[] = [];
  reasons: ReasonChip[] = [];
  marks: PickerMarks | null = null;
  firstDelivery: Day | null = null;
  fieldLabel = '';
  dayValue = '';
  commentRequired = false;
  needsReplaceReason = false;
  rangeText: string | null = null;
  scope: string | null = null;
  warnings: string[] = [];
  errors: string[] = [];
  focusIds: number[] = [];
  canSave = false;
  private seeded = false;

  constructor() {
    super();
    this.update();
  }

  protected update(): void {
    const view = this.store.view;
    if (!view) {
      this.canSave = false;
      this.plan(null);
      return;
    }
    const lines = this.store.rentalLines();
    if (!this.seeded) {
      this.seeded = true;
      for (const id of this.data.lineIds) if (lines.some((l) => l.id === id)) this.checked.add(id);
    }
    for (const id of [...this.checked]) {
      const l = lines.find((x) => x.id === id);
      if (!l || this.blockOf(l)) this.checked.delete(id);
    }
    const sel = lines.filter((l) => this.checked.has(l.id));
    this.groups = memoGroupsOf(lines).map((g) => ({
      key: g.key,
      label: patientLabel(g.memo, this.store.beneficiary),
      lines: g.lines.map((l) => ({ id: l.id, name: lineName(l), note: noteOf(l), why: this.blockOf(l) })),
    }));
    this.marks = pickerMarksOf(sel, { kind: this.bound === 'START' ? 'start' : 'end' });
    this.firstDelivery = minDay(...sel.map(deliveredDay));
    this.leftMonth ??= firstOfMonth(addMonthsClamped(this.bound === 'START' ? (this.firstDelivery ?? view.as_of) : view.as_of, -1));
    this.heads = sel.map((l) => this.headOf(l));
    this.reasons = reasonChips(this.store, this.bound === 'START' ? 'WINDOW_START' : 'WINDOW_END');
    this.commentRequired = this.reasonId !== null && this.store.reasonFlag(this.reasonId, 'comment_required');
    this.needsReplaceReason = this.heads.some((h) => h.effectiveId !== null);
    this.focusIds = sel.map((l) => l.id);
    this.scope = sel.length ? '· ' + sel.map((l) => lineName(l)).join(', ') : null;
    this.rangeText = this.dayText();
    this.fieldLabel = this.bound === 'START'
      ? `Premier jour facturé (avant la livraison${this.firstDelivery ? ' du ' + frShort(this.firstDelivery) : ''})`
      : 'Dernier jour facturé (inclus)';
    this.dayValue = this.day ? frDay(this.day) : '';
    this.warnings = this.warningsOf(sel, view.is_fse);
    this.errors = this.errorsOf(sel);
    const evs = this.events(sel);
    const many = tooManyDecisions(evs.length);
    if (many) this.errors.push(many);
    this.canSave = !this.saving && evs.length > 0 && this.errors.length === 0;
    this.plan(evs);
  }

  // START n'est permis qu'avant le BL et si la carte l'autorise ; une ligne illisible ou neuve ne porte aucune décision.
  private blockOf(l: RentalLineView): string | null {
    if (l.id <= 0) return 'Ligne pas encore enregistrée';
    if (l.lifecycle_unreadable || !l.decisions) return l.anomalies[0] ?? 'Cycle de vie illisible';
    if (lineClosed(l)) return CLOSED_LINE;
    if (this.bound === 'START') {
      if (!l.realdatdel) return 'Ligne pas encore livrée';
      if (l.params?.start_before_delivery !== true) return "Le contrat de location n'autorise pas de facturer avant le BL";
    }
    return null;
  }

  private effectiveId(l: RentalLineView): number | null {
    return (this.bound === 'START' ? l.decisions?.effective_start_id : l.decisions?.effective_end_id) ?? null;
  }

  private boundText(day: Day, head = false): string {
    const t = this.bound === 'START'
      ? `premier jour facturé le ${frDay(day)}`
      : `dernier jour facturé le ${frDay(day)}`;
    return head ? t.charAt(0).toUpperCase() + t.slice(1) : t;
  }

  private headOf(l: RentalLineView): LineHead {
    const effId = this.effectiveId(l);
    const entries = (l.decisions?.window ?? []).filter((w) => w.bound === this.bound).sort((a, b) => a.id - b.id);
    const eff = entries.find((w) => w.id === effId) ?? null;
    const facts = [l.realdatdel ? `livré le ${frDay(deliveredDay(l))}` : 'pas encore livré', l.return_realdatdel ? `repris le ${frDay(returnedDay(l))}` : null].filter((s) => !!s).join(' · ');
    const none = this.bound === 'START' ? 'aucun début corrigé' : 'aucune fin décidée';
    return {
      id: l.id, name: lineName(l), memo: l.memo?.trim() || null, facts,
      current: (this.bound === 'START' ? 'Début actuel : ' : 'Fin actuelle : ') + (eff ? this.boundText(eff.day) : none),
      effectiveId: eff ? eff.id : null,
      history: entries.map((w) => this.entryOf(w, effId)),
    };
  }

  private entryOf(w: RentalWindowDecision, effId: number | null): BoundEntry {
    const comment = w.comment?.trim() ? ` · « ${w.comment.trim()} »` : '';
    return {
      id: w.id,
      what: `n° ${w.id} · ${this.boundText(w.day)} · ${this.store.reasonName(w.reason_id)}${comment}`,
      stamp: stampText(this.store, w.created),
      cancelled: w.cancelled ? cancelledText(this.store, w.cancelled) : null,
      effective: w.id === effId,
    };
  }

  private dayText(): string {
    const d = this.day;
    if (!d) return this.bound === 'START' ? 'Cliquez le premier jour facturé dans le calendrier.' : 'Cliquez le dernier jour facturé dans le calendrier.';
    if (this.bound === 'START') return `Premier jour facturé : ${frWeekday(d)}` + (this.firstDelivery ? `, avant la livraison du ${frWeekday(this.firstDelivery)}` : '');
    return `Dernier jour facturé : ${frWeekday(d)} (inclus)`;
  }

  // Avertissements non bloquants lus dans les bornes appliquées par le serveur (summary, bornes incluses), jamais dans la carte.
  private warningsOf(sel: RentalLineView[], isFse: boolean): string[] {
    const out: string[] = [];
    const notice = isFse && this.reasonId !== null ? this.store.reasonNotice(this.reasonId) : null;
    if (notice) out.push(notice);
    const d = this.day;
    if (this.bound !== 'END' || !d) return out;
    for (const l of sel) {
      const s = l.summary;
      if (s?.window_start && d < s.window_start) out.push(`${lineName(l)} : la fin précède le début de facturation du ${frDay(s.window_start)}`);
      // return_end = dernier jour que facturerait la seule reprise, inclus.
      if (s?.return_end && d > s.return_end) {
        const back = returnedDay(l);
        const after = back ? `la reprise du ${frShort(back)}` : `le dernier jour que facturerait la reprise (${frShort(s.return_end)})`;
        out.push(`${lineName(l)} : la fin décidée est après ${after} ; la facturation continuera jusqu'au ${frShort(d)} (jour choisi, inclus)`);
      }
    }
    return out;
  }

  private errorsOf(sel: RentalLineView[]): string[] {
    const out: string[] = [];
    if (!sel.length) out.push('Aucun matériel coché.');
    if (this.bound === 'START' && this.day && this.firstDelivery && this.day >= this.firstDelivery) {
      out.push(`Le premier jour facturé doit précéder la livraison du ${frDay(this.firstDelivery)}.`);
    }
    if (this.commentRequired && !this.comment.trim()) out.push('Commentaire obligatoire pour ce motif.');
    if (this.needsReplaceReason && !this.replaceReason.trim()) out.push('Motif du remplacement obligatoire.');
    return out;
  }

  // Pas de replace en 2103 : la nouvelle borne (id plus grand) devient effective, puis l'ancienne est annulée ; un refus de l'annulation ne perd pas la borne.
  private events(sel: RentalLineView[]): RentalDecisionEvent[] {
    const out: RentalDecisionEvent[] = [];
    if (!this.day || this.reasonId === null) return out;
    for (const l of sel) {
      out.push(windowAdd(this.data.orderId, l.id, this.bound, this.day, this.reasonId, this.comment));
      const effId = this.effectiveId(l);
      if (effId !== null) out.push(decisionCancel(EventType.RENTAL_WINDOW, this.data.orderId, l.id, effId, this.replaceReason));
    }
    return out;
  }

  groupState(g: ScopeGroup): 'all' | 'some' | 'none' {
    const open = g.lines.filter((l) => !l.why);
    const n = open.filter((l) => this.checked.has(l.id)).length;
    return n === 0 ? 'none' : n === open.length ? 'all' : 'some';
  }

  groupOpen(g: ScopeGroup): boolean {
    return g.lines.some((l) => !l.why);
  }

  toggle(id: number, on: boolean): void {
    if (on) this.checked.add(id);
    else this.checked.delete(id);
    this.update();
  }

  toggleGroup(g: ScopeGroup, on: boolean): void {
    for (const l of g.lines) {
      if (l.why) continue;
      if (on) this.checked.add(l.id);
      else this.checked.delete(l.id);
    }
    this.update();
  }

  async checkAll(): Promise<void> {
    if (this.groups.length >= 2) {
      const r = await Swal.fire({ title: 'Tout cocher', text: `Cocher les ${this.groups.length} résidents ?`, icon: 'question', showCancelButton: true, confirmButtonText: 'Tout cocher', cancelButtonText: 'Annuler' });
      if (!r.isConfirmed) return;
    }
    for (const g of this.groups) for (const l of g.lines) if (!l.why) this.checked.add(l.id);
    this.update();
    this.cdr.markForCheck();
  }

  pickReason(id: number): void {
    this.reasonId = id;
    this.update();
  }

  pickDay(d: Day | null): void {
    this.day = d;
    this.update();
  }

  typed(ev: Event): void {
    const v = this.typedDay(ev, this.leftMonth);
    if (v === undefined) return;
    this.day = v;
    if (v) {
      (ev.target as HTMLInputElement).value = frDay(v);
      this.leftMonth = monthShowing(this.leftMonth, v);
    }
    this.update();
  }

  setComment(v: string): void {
    this.comment = v;
    this.update();
  }

  setReplaceReason(v: string): void {
    this.replaceReason = v;
    this.update();
  }

  setLeftMonth(d: Day): void {
    this.leftMonth = d;
  }

  // Annulation seule de la borne effective : cette ligne cochée, les bornes identiques des voisins de même mémo proposées décochées.
  cancelBound(lineId: number): void {
    const l = this.store.line(lineId);
    const effId = l ? this.effectiveId(l) : null;
    const eff = l?.decisions?.window.find((w) => w.id === effId);
    if (!l || !eff) return;
    const twins = memoTwins(this.store.rentalLines(), l.id, eff.id, (x) => x.decisions?.window, windowTwinKey);
    this.nested(this.dialogs.openCancelDecision({
      store: this.store, orderId: this.data.orderId, typ: EventType.RENTAL_WINDOW,
      targets: [{ lineId: l.id, id: eff.id, checked: true }, ...twins],
      title: this.bound === 'START' ? 'Annuler le début corrigé' : 'Annuler la fin de facturation',
      summary: `${this.boundText(eff.day, true)} · ${this.store.reasonName(eff.reason_id)} · ${lineName(l)}`,
    }));
  }

  save(): void {
    if (!this.canSave) return;
    this.send(this.events(this.store.rentalLines().filter((l) => this.checked.has(l.id))), this.bound === 'START' ? 'Début de facturation' : 'Fin de facturation', this.bound === 'START' ? 'm' : 'f');
  }
}

function noteOf(l: RentalLineView): string {
  const billed = l.billed_until ? `facturé jusqu'au ${frShort(l.billed_until)}` : 'rien de facturé';
  return billed + (l.return_realdatdel ? ` · repris le ${frShort(returnedDay(l))}` : '') + ` · ${statusLabel(l.status)}`;
}
